/**
 * EligibilityReviewResults — the results page for one AI eligibility review
 * (/crm/contacts/:id/reviews/:reviewId), laid out as an Inato-style split view:
 *
 *   LEFT (~45%): the "Needs your review" quick-verify queue (ReviewQueuePanel,
 *   inline Confirm/Edit — no scroll-hunting), filter pills [All / Needs
 *   attention / Eligible] + the "I/E CRITERIA MET" summary chips, then the
 *   Inclusion / Exclusion sections of impact-verdict criterion rows
 *   (CriteriaListPanel), then the sign-off bar and the collapsible "What the
 *   AI read" / run-details footers.
 *
 *   RIGHT (~55%, sticky): the source viewer (SourceViewerPane) — an embedded
 *   browser-native PDF viewer over same-origin blob bytes with a document
 *   picker; document citations jump to their page, non-document citations swap
 *   in the dossier-segment text view. On mobile the panes stack behind
 *   Results / Sources tabs and a citation tap switches to Sources.
 *
 * Kept from the previous layout: the limitations acknowledgment gate
 * (localStorage, MHR precedent), the overall banner (verdict + study title +
 * override-count chip), the sign-off checklist rule, the stalled-review
 * notice, failed/cancelled states, and 4s polling while non-terminal. New:
 * the "View AI prompt" header button (PromptViewerModal) and one-click
 * [Confirm] verify-agree on every row (reuses the verify endpoint with
 * human_result = AI result); [Edit] opens the existing verify/override modal.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  ClipboardList,
  FileSearch,
  FileWarning,
  HelpCircle,
  Loader2,
  ScrollText,
  Search,
  ShieldCheck,
  Users,
  XCircle,
  Zap,
} from 'lucide-react';
import api from '../../services/api';
import {
  EligibilityCitation,
  EligibilityCoverage,
  EligibilityCriterionResult,
  EligibilityCriterionResultValue,
  EligibilityOverallResult,
  EligibilityReviewDetailResponse,
  EligibilityReviewDocument,
  EligibilityReviewStatus,
} from '../types';
import { dateTime, relTime } from '../lib';
import { useConfirm, Z } from '../components/ui';
import { touchTarget } from '../components/ui/buttons';
import PageHeader from '../components/PageHeader';
import CriteriaListPanel from '../components/eligibility/CriteriaListPanel';
import { effectiveResult } from '../components/eligibility/CriterionRow';
import SourceViewerPane, { SourceView } from '../components/eligibility/SourceViewerPane';
import PromptViewerModal from '../components/eligibility/PromptViewerModal';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

const TERMINAL_STATUSES = new Set<EligibilityReviewStatus>(['completed', 'failed', 'cancelled']);

/** A non-terminal review older than this is considered stalled. */
const STALL_MS = 30 * 60 * 1000;

const STATUS_LABELS: Record<EligibilityReviewStatus, string> = {
  queued: 'Queued',
  building_dossier: 'Building the dossier',
  analyzing: 'Analyzing criteria',
  verifying: 'Verification pass',
  // Facts engine (engine==='facts'|'shadow') in-flight states.
  extracting_facts: 'Extracting facts…',
  evaluating: 'Evaluating criteria…',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const DISCLAIMER =
  'AI-assisted pre-screening aid — not a medical or enrollment determination. Verify every criterion against the sources.';

const OVERALL_BANNERS: Record<EligibilityOverallResult, { label: string; cls: string; icon: typeof CheckCircle }> = {
  eligible: {
    label: 'Potentially eligible',
    cls: 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300',
    icon: CheckCircle,
  },
  excluded: {
    label: 'Likely excluded',
    cls: 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300',
    icon: XCircle,
  },
  review_required: {
    label: 'Needs review',
    cls: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300',
    icon: HelpCircle,
  },
  incomplete: {
    label: 'Incomplete',
    cls: 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300',
    icon: AlertTriangle,
  },
};

const RESULT_OPTIONS: { value: EligibilityCriterionResultValue; label: string }[] = [
  { value: 'met', label: 'Met' },
  { value: 'not_met', label: 'Not met' },
  { value: 'not_applicable', label: 'Not applicable (criterion cannot apply to this patient)' },
  { value: 'uncertain', label: 'Uncertain' },
  { value: 'not_found', label: 'Not found' },
  { value: 'contradictory', label: 'Contradictory' },
];

/**
 * Fallback chips rendered straight from the stored AI counts (only when no
 * per-criterion rows came back). The aggregate met/not_met mix inclusion and
 * exclusion criteria, so their eligibility impact is unknowable here — they
 * render NEUTRAL gray, never success/failure colors; the explicit
 * exclusions_met stays red.
 */
const COUNT_CHIPS: { key: string; label: string; cls: string }[] = [
  { key: 'met', label: 'Met', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  { key: 'not_met', label: 'Not met', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  { key: 'uncertain', label: 'Uncertain', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  { key: 'not_found', label: 'Not found', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  { key: 'contradictory', label: 'Contradictory', cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  { key: 'not_applicable', label: 'Not applicable', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  { key: 'exclusions_met', label: 'Exclusions met', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
];

const LIMITATIONS: { icon: typeof ClipboardList; title: string; description: string }[] = [
  {
    icon: ClipboardList,
    title: 'This tool is for PRE-SCREENING ASSISTANCE only',
    description: 'It does not replace clinical judgment or formal eligibility determination.',
  },
  {
    icon: Users,
    title: 'All results REQUIRE human review',
    description: 'No patient decisions should be made based solely on AI analysis.',
  },
  {
    icon: AlertCircle,
    title: 'AI may miss or misinterpret information',
    description: 'The system analyzes what is on file but may not capture every relevant detail.',
  },
  {
    icon: Search,
    title: "'Not found' does NOT mean absence",
    description: 'It only means no evidence was found in the sources on file — the condition may exist in records not provided.',
  },
  {
    icon: FileSearch,
    title: 'Always verify against the sources',
    description: 'Use every citation as a starting point, not a final determination.',
  },
];

const fmtUsd = (n: unknown): string | null => {
  const v = Number(n);
  return Number.isFinite(v) ? `$${v.toFixed(2)}` : null;
};

const fmtInt = (n: unknown): string => {
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString() : '0';
};

// ---------------------------------------------------------------------------
// Limitations acknowledgment gate (blocks results until acknowledged once per
// review — localStorage `crm-elig-limitations-<reviewId>`, MHR precedent).
// ---------------------------------------------------------------------------
function LimitationsGate({ backTo, onAcknowledge }: { backTo: string; onAcknowledge: () => void }) {
  const [checked, setChecked] = useState(false);
  return (
    <div className={`fixed inset-0 ${Z.modal} overflow-y-auto`}>
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="elig-limitations-title"
          className="relative w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800"
        >
          <div className="flex items-center gap-3 border-b border-gray-200 bg-amber-50 px-4 py-4 dark:border-gray-700 dark:bg-amber-900/20 sm:px-6">
            <div className="rounded-lg bg-amber-100 p-2 dark:bg-amber-900/40">
              <AlertTriangle size={22} className="text-amber-600 dark:text-amber-400" />
            </div>
            <h2 id="elig-limitations-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Important limitations — please read carefully
            </h2>
          </div>
          <div className="max-h-[55vh] space-y-3 overflow-y-auto px-4 py-4 sm:px-6">
            {LIMITATIONS.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="flex items-start gap-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-900/40">
                  <div className="shrink-0 rounded-lg bg-amber-100 p-2 dark:bg-amber-900/40">
                    <Icon size={18} className="text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.title}</h3>
                    <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border-t border-gray-200 bg-gray-50 px-4 py-4 dark:border-gray-700 dark:bg-gray-900/40 sm:px-6">
            <label className="flex cursor-pointer select-none items-start gap-3">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="mt-0.5 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">I have read and understand these limitations</span>
            </label>
            <div className="mt-4 flex items-center justify-end gap-2">
              <Link
                to={backTo}
                className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Back to lead
              </Link>
              <button
                type="button"
                onClick={() => { if (checked) onAcknowledge(); }}
                disabled={!checked}
                className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
              >
                Continue to results
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Verify / override modal — the human check on one criterion. Overriding the
// AI result REQUIRES a reason (audited server-side). Re-opening an existing
// verification pre-fills it (the server allows re-verification any time
// before sign-off), so a mis-click can be corrected.
// ---------------------------------------------------------------------------
function VerifyModal({
  reviewId,
  result,
  onClose,
  onSaved,
}: {
  reviewId: string;
  result: EligibilityCriterionResult;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [humanResult, setHumanResult] = useState<EligibilityCriterionResultValue>(
    (result.human_verified && result.human_result) || result.result);
  const [reason, setReason] = useState(result.human_override_reason || '');
  const [notes, setNotes] = useState(result.human_notes || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOverride = humanResult !== result.result;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const submit = async () => {
    if (busy) return;
    if (isOverride && !reason.trim()) {
      setError('An override reason is required when your result differs from the AI result.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, string> = { human_result: humanResult };
      if (isOverride) body.override_reason = reason.trim();
      if (notes.trim()) body.notes = notes.trim();
      await api.post(`/api/crm/eligibility-reviews/${reviewId}/results/${result.id}/verify`, body);
      onSaved();
    } catch (e: any) {
      setError(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 ${Z.modal} flex items-center justify-center bg-black/50 p-4`}
      onClick={() => { if (!busy) onClose(); }}
      role="presentation"
    >
      <div
        className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Verify criterion"
      >
        <div className="border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
            <ShieldCheck className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            {result.human_verified ? 'Edit verification of' : 'Verify'}{' '}
            {result.criterion_number ? `criterion ${result.criterion_number}` : 'criterion'}
          </h2>
          {result.criterion_text && (
            <p className="mt-1 max-h-28 overflow-y-auto break-words text-sm text-gray-600 dark:text-gray-400">
              {result.criterion_text}
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Your result</p>
          <div className="mt-1.5 space-y-1">
            {RESULT_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex ${touchTarget} cursor-pointer items-center gap-2.5 rounded-lg border p-2.5 text-sm transition-colors duration-150 ${
                  humanResult === opt.value
                    ? 'border-primary-300 bg-primary-50/60 text-gray-900 dark:border-primary-700 dark:bg-primary-900/10 dark:text-gray-100'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/40'
                }`}
              >
                <input
                  type="radio"
                  name="human-result"
                  value={opt.value}
                  checked={humanResult === opt.value}
                  onChange={() => setHumanResult(opt.value)}
                  className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                />
                <span className="flex-1">{opt.label}</span>
                {opt.value === result.result && (
                  <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">AI result</span>
                )}
              </label>
            ))}
          </div>

          {isOverride && (
            <>
              <label className="mt-3 block text-xs font-medium text-gray-600 dark:text-gray-300">
                Override reason <span className="text-rose-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 1000))}
                rows={2}
                placeholder="Why does your determination differ from the AI's?"
                className="mt-1 w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              />
            </>
          )}

          <label className="mt-3 block text-xs font-medium text-gray-600 dark:text-gray-300">
            Notes <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 1000))}
            rows={2}
            placeholder="Anything worth keeping with this verification…"
            className="mt-1 w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 p-4 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || (isOverride && !reason.trim())}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? 'Saving…' : isOverride ? 'Save override' : 'Confirm result'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Collapsible footer card (coverage / run details).
// ---------------------------------------------------------------------------
function CollapsibleCard({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`flex ${touchTarget} w-full items-center justify-between gap-2 px-4 py-3 text-left transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:hover:bg-gray-700/40`}
      >
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</span>
        {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>
      {open && <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-700">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function EligibilityReviewResults() {
  const { id = '', reviewId = '' } = useParams();
  const confirm = useConfirm();
  const backTo = `/crm/contacts/${id}`;
  const lsKey = `crm-elig-limitations-${reviewId}`;

  const [acknowledged, setAcknowledged] = useState<boolean>(() => {
    try { return localStorage.getItem(lsKey) === '1'; } catch { return false; }
  });
  // Params-only navigation keeps this page mounted — re-read the gate when the
  // review changes so a different review shows its own acknowledgment.
  useEffect(() => {
    try { setAcknowledged(localStorage.getItem(lsKey) === '1'); } catch { setAcknowledged(false); }
  }, [lsKey]);
  const [detail, setDetail] = useState<EligibilityReviewDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verifyTarget, setVerifyTarget] = useState<EligibilityCriterionResult | null>(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const [signAck, setSignAck] = useState(false);
  const [signBusy, setSignBusy] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  /** Live clock for stall detection — advanced on every poll tick. */
  const [now, setNow] = useState<number>(() => Date.now());
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Split-view state: what the right pane shows + which stacked tab is active
  // on mobile. The nonce keys the viewer iframe so a re-jump forces a reload.
  const [sourceView, setSourceView] = useState<SourceView | null>(null);
  const [mobileTab, setMobileTab] = useState<'results' | 'sources'>('results');
  const nonceRef = useRef(0);
  const lastDocRef = useRef<{ documentId: string; page?: number } | null>(null);

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const res = await api.get(`/api/crm/eligibility-reviews/${reviewId}`);
      setDetail(res.data);
      setError(null);
    } catch (e: any) {
      if (initial) setError(errMsg(e));
    } finally {
      if (initial) setLoading(false);
    }
  }, [reviewId]);

  useEffect(() => { load(true); }, [load]);
  // Route-param change (page stays mounted): reset everything review-scoped.
  useEffect(() => {
    setCancelError(null);
    setCancelBusy(false);
    setSignAck(false);
    setSignError(null);
    setVerifyTarget(null);
    setPromptOpen(false);
    setSourceView(null);
    setMobileTab('results');
    lastDocRef.current = null;
  }, [reviewId]);

  // Poll every 4s while the review is still running (shows the pass status).
  // A non-terminal review whose created_at is >30 min old is treated as
  // STALLED: drop to a slow 60s poll and render the stalled notice instead of
  // spinning forever (worker retries are exhausted well within 30 minutes).
  const status = detail?.review?.status;
  const createdAtMs = detail?.review?.created_at ? new Date(detail.review.created_at).getTime() : NaN;
  const stalled =
    !!status && !TERMINAL_STATUSES.has(status) && Number.isFinite(createdAtMs) && now - createdAtMs > STALL_MS;
  useEffect(() => {
    if (!status || TERMINAL_STATUSES.has(status)) return;
    const timer = window.setInterval(() => {
      setNow(Date.now());
      load(false);
    }, stalled ? 60000 : 4000);
    return () => window.clearInterval(timer);
  }, [status, stalled, load]);

  const cancelReview = async () => {
    if (cancelBusy) return;
    if (!(await confirm({
      title: 'Cancel this queued review?',
      message: 'It has not started analyzing and nothing will be billed.',
      confirmLabel: 'Cancel review',
      cancelLabel: 'Keep it',
      danger: true,
    }))) return;
    setCancelBusy(true);
    setCancelError(null);
    try {
      await api.post(`/api/crm/eligibility-reviews/${reviewId}/cancel`, {});
      await load(false);
    } catch (e: any) {
      // 409 = the queued-only guard: the review started (or finished) while
      // the confirm dialog was open — refresh so the page shows the truth.
      setCancelError(
        e?.response?.status === 409
          ? (e?.response?.data?.detail || 'Only queued reviews can be cancelled — this one has already started.')
          : errMsg(e));
      await load(false);
    } finally {
      setCancelBusy(false);
    }
  };

  const acknowledge = () => {
    try { localStorage.setItem(lsKey, '1'); } catch { /* private mode — gate re-shows next visit */ }
    setAcknowledged(true);
  };

  const review = detail?.review || null;
  const results = useMemo(() => detail?.results || [], [detail]);
  const signedOff = !!review?.signed_off_at;
  const canVerify = review?.status === 'completed' && !signedOff;

  // The detail in state can briefly belong to the PREVIOUS review while a
  // params-only navigation is loading — never seed viewer state from it.
  const detailIsCurrent = detail?.review?.id === reviewId;
  const documents: EligibilityReviewDocument[] = useMemo(
    () => (detailIsCurrent && detail?.documents) || [], [detailIsCurrent, detail]);
  const documentNames = useMemo(() => {
    const m: Record<string, string> = {};
    for (const d of documents) if (d.filename) m[d.id] = d.filename;
    return m;
  }, [documents]);

  // Default the right pane to the first document once the detail arrives.
  useEffect(() => {
    if (!detailIsCurrent || sourceView || documents.length === 0) return;
    setSourceView({ kind: 'document', documentId: documents[0].id, nonce: 0 });
    lastDocRef.current = { documentId: documents[0].id };
  }, [detailIsCurrent, documents, sourceView]);

  const openCitation = (c: EligibilityCitation) => {
    const src = c.source;
    if (src?.type === 'document_page' && src.document_id) {
      nonceRef.current += 1;
      lastDocRef.current = { documentId: src.document_id, page: src.page };
      setSourceView({ kind: 'document', documentId: src.document_id, page: src.page, nonce: nonceRef.current });
      setMobileTab('sources');
    } else if (c.segment_number != null) {
      // Legacy dossier citation — fetches GET .../segments/:n. Facts-engine
      // citations (segment_number null, non-document source) never reach
      // here: CriterionRow renders those as an inline expandable excerpt and
      // does not call onCitationClick for them.
      setSourceView({ kind: 'segment', segmentNumber: c.segment_number });
      setMobileTab('sources');
    }
  };

  const selectDocument = (documentId: string) => {
    nonceRef.current += 1;
    lastDocRef.current = { documentId };
    setSourceView({ kind: 'document', documentId, nonce: nonceRef.current });
  };

  const backToDocuments = () => {
    const target = lastDocRef.current || (documents[0] ? { documentId: documents[0].id } : null);
    if (!target) { setSourceView(null); return; }
    nonceRef.current += 1;
    setSourceView({ kind: 'document', ...target, nonce: nonceRef.current });
  };

  /** One-click verify-agree: records human_result = the AI result. */
  const confirmResult = useCallback(async (r: EligibilityCriterionResult) => {
    await api.post(`/api/crm/eligibility-reviews/${reviewId}/results/${r.id}/verify`, {
      human_result: r.result,
    });
    await load(false);
  }, [reviewId, load]);

  // Sign-off checklist: every uncertain/contradictory (or flagged) criterion
  // must be human-verified before the review can be signed.
  const needingReview = useMemo(
    () => results.filter((r) =>
      ['uncertain', 'contradictory'].includes(effectiveResult(r)) || r.requires_human_review),
    [results]);
  const allReviewed = needingReview.every((r) => r.human_verified);

  // Human overrides — the stored overall_result is AI-computed and does NOT
  // account for them, so the banner must say so whenever any exist.
  const overriddenCount = useMemo(
    () => results.filter((r) => r.human_verified && !!r.human_result && r.human_result !== r.result).length,
    [results]);

  const signOff = async () => {
    if (signBusy) return;
    setSignBusy(true);
    setSignError(null);
    try {
      await api.post(`/api/crm/eligibility-reviews/${reviewId}/sign-off`, {});
      await load(false);
    } catch (e: any) {
      // Server-side enforcement (defense in depth behind the client-side
      // checklist): 409 SIGN_OFF_BLOCKED means flagged criteria are still
      // unverified — say so plainly and refresh in case another user's
      // verifications changed underneath us.
      const data = e?.response?.data;
      if (data?.error === 'SIGN_OFF_BLOCKED') {
        const n = Number(data.unverified_count);
        setSignError(
          Number.isFinite(n) && n > 0
            ? `Sign-off blocked: ${n} ${n === 1 ? 'criterion still needs' : 'criteria still need'} human verification. Verify each flagged criterion above, then try again.`
            : 'Sign-off blocked: some criteria still need human verification. Verify each flagged criterion above, then try again.'
        );
        await load(false);
      } else {
        setSignError(errMsg(e));
      }
    } finally {
      setSignBusy(false);
    }
  };

  const counts = review?.counts || null;
  const coverage: EligibilityCoverage | null = counts?.coverage || null;
  const excludedSources = detail?.dossier?.excluded_sources || [];
  // Facts engine (docs/crm-eligibility-facts-plan.md §3/§6): no dossier is
  // built, so "What the AI read" / coverage stay null here — tolerated
  // everywhere above via optional chaining. counts.extraction carries the
  // per-document fact-extraction outcome instead.
  const isFactsEngine = review?.engine === 'facts';
  const extraction = counts?.extraction || null;
  const documentsFailed = extraction?.documents_failed || [];
  const banner = review?.overall_result ? OVERALL_BANNERS[review.overall_result] : null;
  const BannerIcon = banner?.icon || HelpCircle;
  const actualCost = fmtUsd(review?.actual_cost);
  const estimatedCost = fmtUsd(review?.estimated_cost);

  return (
    <div className="w-full">
      {!acknowledged && <LimitationsGate backTo={backTo} onAcknowledge={acknowledge} />}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <Link
          to={backTo}
          className="inline-flex min-h-[44px] items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100 sm:min-h-0"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to lead
        </Link>
        {review && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-400">
              Started {relTime(review.created_at)}
              {review.criteria_set_version != null ? ` · criteria v${review.criteria_set_version}` : ''}
            </span>
            <button
              type="button"
              onClick={() => setPromptOpen(true)}
              title="See the AI prompts this review pipeline uses — and which version ran"
              className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100 sm:min-h-0"
            >
              <ScrollText size={13} />
              View AI prompt
            </button>
          </div>
        )}
      </div>

      <PageHeader
        title={
          <span className="inline-flex min-w-0 items-center gap-2">
            <span className="truncate">
              AI eligibility review{review?.study_title ? ` — ${review.study_title}` : ''}
            </span>
            {isFactsEngine && (
              <span
                title="This review ran on the facts engine — compiled rules + tiered AI retrieval over extracted, cited facts (no whole-record dossier read)."
                className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-primary-300 bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 dark:border-primary-700 dark:bg-primary-900/20 dark:text-primary-400"
              >
                <Zap className="h-3 w-3" />
                Facts engine
              </span>
            )}
          </span>
        }
        subtitle={DISCLAIMER}
      />

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
        </div>
      ) : error ? (
        <div role="alert" className="mt-4 max-w-4xl rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      ) : !review ? (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">This review is unavailable.</p>
      ) : !TERMINAL_STATUSES.has(review.status) ? (
        // ------------------------------------------------------------------
        // In flight — pass status + progress; the poll refreshes this.
        // ------------------------------------------------------------------
        <div className="mt-6 max-w-4xl rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {STATUS_LABELS[review.status] || review.status}…
              </p>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {review.status === 'analyzing' && review.criteria_total
                  ? `Criterion ${fmtInt(review.criteria_evaluated || 0)} of ${fmtInt(review.criteria_total)} evaluated.`
                  : 'This page refreshes automatically every few seconds.'}
              </p>
            </div>
          </div>
          {review.status === 'analyzing' && !!review.criteria_total && (
            <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
              <div
                className="h-full rounded-full bg-primary-500 transition-all duration-300"
                style={{ width: `${Math.min(100, Math.round(((review.criteria_evaluated || 0) / review.criteria_total) * 100))}%` }}
              />
            </div>
          )}
          {stalled && (
            <div
              role="alert"
              className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                This review appears stalled — it started {relTime(review.created_at)} and has not completed.
                {review.status === 'queued'
                  ? ' You can cancel it below and run a new one from the lead profile.'
                  : ' If it never completes, run a new review from the lead profile.'}
              </span>
            </div>
          )}
          {cancelError && (
            <div
              role="alert"
              className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
            >
              {cancelError}
            </div>
          )}
          {review.status === 'queued' && (
            <button
              type="button"
              onClick={cancelReview}
              disabled={cancelBusy}
              className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 transition-colors duration-150 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-800 dark:bg-gray-800 dark:text-red-400 dark:hover:bg-red-900/20 sm:min-h-0"
            >
              {cancelBusy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {cancelBusy ? 'Cancelling…' : 'Cancel review'}
            </button>
          )}
          <p className="mt-4 text-xs text-gray-400">
            A running review completes even if you leave this page.
            {review.status === 'queued' ? ' Only queued reviews can be cancelled.' : ''}
          </p>
        </div>
      ) : review.status === 'failed' ? (
        <div role="alert" className="mt-6 max-w-4xl rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <p className="font-semibold">This review failed.</p>
          {review.error && <p className="mt-1">{review.error}</p>}
          <p className="mt-1 text-xs">You can run a new review from the lead's Profile tab.</p>
        </div>
      ) : review.status === 'cancelled' ? (
        <div className="mt-6 max-w-4xl rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300">
          This review was cancelled before it ran.
        </div>
      ) : (
        // ------------------------------------------------------------------
        // Completed — Inato-style split view.
        // ------------------------------------------------------------------
        <div className="mt-6 space-y-4">
          {banner && (
            <div className={`flex items-start gap-3 rounded-xl border p-4 ${banner.cls}`}>
              <BannerIcon className="mt-0.5 h-6 w-6 shrink-0" />
              <div className="min-w-0">
                <p className="break-words text-base font-semibold">
                  {banner.label}
                  {review.study_title ? <span className="font-normal opacity-90"> — {review.study_title}</span> : null}
                </p>
                {review.summary && <p className="mt-1 break-words text-sm">{review.summary}</p>}
                {overriddenCount > 0 && (
                  <p className="mt-2">
                    <span className="inline-flex items-start gap-1.5 rounded-full border border-amber-300 bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-900 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                      <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      {overriddenCount} {overriddenCount === 1 ? 'criterion' : 'criteria'} human-overridden — the
                      AI overall result may not reflect overrides
                    </span>
                  </p>
                )}
                <p className="mt-2 text-xs opacity-80">{DISCLAIMER}</p>
              </div>
            </div>
          )}

          {/* Facts engine only — no dossier to report coverage on; this is
              the equivalent notice for documents the extraction step could
              not read (excluded_sources' counterpart). */}
          {documentsFailed.length > 0 && (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20"
            >
              <FileWarning className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="min-w-0 text-sm text-amber-800 dark:text-amber-300">
                <p className="font-medium">
                  {documentsFailed.length} of {extraction?.documents_total ?? '?'} documents could not be
                  read — criteria relying on {documentsFailed.length === 1 ? 'it' : 'them'} were flagged for
                  human review.
                </p>
                <ul className="mt-1.5 space-y-0.5 pl-5 text-xs">
                  {documentsFailed.map((d, i) => (
                    <li key={d.document_id || i} className="list-disc">
                      {d.filename || documentNames[d.document_id] || d.document_id || 'Unknown document'}
                      {d.reason ? ` — ${d.reason}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Mobile: stacked tabs switch between the two panes. */}
          <div className="flex gap-1.5 lg:hidden" role="tablist" aria-label="Results or sources">
            {(['results', 'sources'] as const).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={mobileTab === t}
                onClick={() => setMobileTab(t)}
                className={`inline-flex ${touchTarget} flex-1 items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  mobileTab === t
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {t === 'results' ? 'Results' : 'Sources'}
              </button>
            ))}
          </div>

          <div className="lg:grid lg:grid-cols-[45fr_55fr] lg:items-start lg:gap-4">
            {/* LEFT: criteria list + sign-off + footers */}
            <div className={`min-w-0 space-y-4 ${mobileTab === 'sources' ? 'hidden lg:block' : ''}`}>
              {results.length > 0 ? (
                <CriteriaListPanel
                  results={results}
                  documentNames={documentNames}
                  onCitationClick={openCitation}
                  onConfirm={canVerify ? confirmResult : undefined}
                  onEdit={canVerify ? setVerifyTarget : undefined}
                  sourceStale={detail?.criteria_source_stale === true}
                />
              ) : (
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No criterion results were recorded for this review.
                  </p>
                  {counts && (
                    // Fallback: the stored AI counts (met/not_met mix inclusion
                    // + exclusion here, so they stay neutral gray except the
                    // explicit exclusions_met).
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {COUNT_CHIPS.map(({ key, label, cls }) => {
                        const v = Number((counts as Record<string, unknown>)[key]);
                        if (!Number.isFinite(v)) return null;
                        return (
                          <span key={key} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>
                            {label}: {v}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Sign-off bar */}
              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                {signedOff ? (
                  <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                    <ShieldCheck className="h-5 w-5" />
                    <span>
                      Review signed off {review.signed_off_at ? dateTime(review.signed_off_at) : ''}. Verification is frozen.
                    </span>
                  </div>
                ) : (
                  <>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Sign off this review</h2>
                    <div className="mt-2 flex items-start gap-2 text-sm">
                      {allReviewed ? (
                        <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
                      ) : (
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                      )}
                      <p className="text-gray-700 dark:text-gray-300">
                        All uncertain or contradictory criteria reviewed
                        {needingReview.length > 0 && (
                          <span className="text-gray-400">
                            {' '}({needingReview.filter((r) => r.human_verified).length} of {needingReview.length} verified)
                          </span>
                        )}
                      </p>
                    </div>
                    {!allReviewed && (
                      <p className="mt-1 pl-6 text-xs text-amber-700 dark:text-amber-400">
                        Verify each uncertain, contradictory or flagged criterion above before signing off.
                      </p>
                    )}
                    {signError && (
                      <div role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                        {signError}
                      </div>
                    )}
                    <label className="mt-3 flex cursor-pointer select-none items-start gap-2.5">
                      <input
                        type="checkbox"
                        checked={signAck}
                        onChange={(e) => setSignAck(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        I reviewed these AI results against the sources and understand this is a
                        pre-screening aid, not an eligibility determination.
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={signOff}
                      disabled={signBusy || !signAck || !allReviewed}
                      className="mt-3 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
                    >
                      {signBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                      {signBusy ? 'Signing off…' : 'Sign off review'}
                    </button>
                  </>
                )}
              </div>

              {/* Coverage + excluded sources (collapsible). Facts-engine reviews
                  have no dossier (documentIsCurrent/detail.dossier is null) —
                  every dossier-specific line below tolerates that and this
                  card still opens to show the facts-used summary + the
                  criteria-evaluated line. */}
              {(coverage || excludedSources.length > 0 || detail?.dossier || isFactsEngine) && (
                <CollapsibleCard title="What the AI read" defaultOpen={excludedSources.length > 0 || documentsFailed.length > 0}>
                  {/* Sources reviewed — the recruiter-chosen scope this review ran
                      against (source-scoping addendum). Absent on older reviews ⇒
                      everything on file was in scope. */}
                  {(() => {
                    const sel = review.source_selection;
                    const docNameById = new Map(documents.map((d) => [d.id, d.filename || 'Untitled record']));
                    let recordsPart: string;
                    let recordsTitle: string | undefined;
                    if (!sel) {
                      return (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-medium">Sources reviewed:</span> everything on file — medical records,
                          patient communications, and forms.
                        </p>
                      );
                    }
                    const mr = sel.medical_records || {};
                    if (mr.enabled === false) {
                      recordsPart = 'medical records not included';
                    } else {
                      const ids = Array.isArray(mr.document_ids) ? mr.document_ids : [];
                      recordsPart = `${ids.length} medical record${ids.length === 1 ? '' : 's'}`;
                      recordsTitle = ids.map((id) => docNameById.get(id) || id).join('\n') || undefined;
                    }
                    const commsOn = sel.communications?.enabled === true;
                    const formsOn = sel.forms?.enabled === true;
                    return (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Sources reviewed:</span>{' '}
                        <span title={recordsTitle}>{recordsPart}</span>
                        {` · communications ${commsOn ? 'included' : 'not included'}`}
                        {` · forms ${formsOn ? 'included' : 'not included'}`}
                        <span className="text-gray-400"> — identity &amp; recruiter-entered profile always included.</span>
                      </p>
                    );
                  })()}
                  {detail?.dossier?.segment_count != null && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Dossier of {fmtInt(detail.dossier.segment_count)} segments
                      {detail.dossier.built_at ? `, assembled ${dateTime(detail.dossier.built_at)}` : ''}.
                    </p>
                  )}
                  {isFactsEngine && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Facts engine: no whole-record dossier is built — criteria are evaluated from extracted,
                      cited facts plus targeted record retrieval.
                      {extraction?.facts_used != null ? ` ${fmtInt(extraction.facts_used)} facts used.` : ''}
                      {extraction?.documents_total != null
                        ? ` ${fmtInt(extraction.documents_ok ?? (extraction.documents_total - documentsFailed.length))} of ${fmtInt(extraction.documents_total)} documents read successfully.`
                        : ''}
                    </p>
                  )}
                  {review.criteria_total != null && (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {fmtInt(review.criteria_evaluated || 0)} of {fmtInt(review.criteria_total)} criteria evaluated.
                    </p>
                  )}
                  {coverage && (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      Coverage: {[
                        // Numeric coverage stats, key → readable label.
                        ...Object.entries(coverage)
                          .filter(([k, v]) => k !== 'verification_ran' && typeof v === 'number' && Number.isFinite(v))
                          .map(([k, v]) => `${k.replace(/_/g, ' ')} ${fmtInt(v)}`),
                        // The verification pass is a boolean — say ran/skipped, not true/false.
                        typeof coverage.verification_ran === 'boolean'
                          ? `Verification pass: ${coverage.verification_ran ? 'ran' : 'skipped'}`
                          : null,
                      ].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {excludedSources.length > 0 && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
                      <p className="flex items-center gap-1.5 text-xs font-medium text-amber-800 dark:text-amber-300">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Some information could not be included in the AI's reading:
                      </p>
                      <ul className="mt-1.5 space-y-0.5 pl-5 text-xs text-amber-800 dark:text-amber-300">
                        {excludedSources.map((s, i) => (
                          <li key={i} className="list-disc">
                            {[s.type, s.reason, s.detail].filter(Boolean).join(' — ')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CollapsibleCard>
              )}

              {/* Cost / tokens footer (collapsible) */}
              <CollapsibleCard title="Run details">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {[
                    review.model_id ? `Model ${review.model_id}` : null,
                    actualCost ? `cost ${actualCost}` : estimatedCost ? `estimated cost ${estimatedCost}` : null,
                    `tokens in ${fmtInt(review.input_tokens)} / out ${fmtInt(review.output_tokens)}`,
                    Number(review.cached_input_tokens) > 0 ? `cached ${fmtInt(review.cached_input_tokens)}` : null,
                    review.completed_at ? `completed ${dateTime(review.completed_at)}` : null,
                  ].filter(Boolean).join(' · ')}
                </p>
              </CollapsibleCard>
            </div>

            {/* RIGHT: source viewer (sticky on desktop) */}
            <div
              className={`mt-4 min-w-0 lg:sticky lg:top-4 lg:mt-0 ${mobileTab === 'results' ? 'hidden lg:block' : ''}`}
            >
              <SourceViewerPane
                key={reviewId}
                reviewId={reviewId}
                documents={documents}
                view={sourceView}
                onSelectDocument={selectDocument}
                onBackToDocuments={backToDocuments}
              />
            </div>
          </div>
        </div>
      )}

      {verifyTarget && (
        <VerifyModal
          reviewId={reviewId}
          result={verifyTarget}
          onClose={() => setVerifyTarget(null)}
          onSaved={() => { setVerifyTarget(null); load(false); }}
        />
      )}
      {promptOpen && <PromptViewerModal reviewId={reviewId} onClose={() => setPromptOpen(false)} />}
    </div>
  );
}
